import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActiveCourse } from '../context/ActiveCourseContext';
import { courseAPI } from '../utils/api';
import Navbar from '../components/Navbar';
import { Lightbulb, BookOpen, TrendingUp, Target, Plus, ChevronRight, BarChart2, Sparkles, X, Clock, Award, Layers } from 'lucide-react';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user, updateUser, isAuthenticated } = useAuth();
  const { activeCourseId, activeCourseData, setActiveCourse, validateAndClearIfInvalid } = useActiveCourse();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(false); // Changed to false to prevent initial blink
  const [demoMode, setDemoMode] = useState(false);
  const [showAllCourses, setShowAllCourses] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedSemester, setSelectedSemester] = useState('Recent');
  const [visibleCount, setVisibleCount] = useState(4);
  const hasFetchedData = useRef(false); // Track if we've fetched data in this session

  // Helper: Get recent subjects order from localStorage
  const getRecentOrder = () => {
    try {
      const stored = localStorage.getItem('recentSubjects');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  };

  // Helper: Get time remaining (dummy - 1 month subscription) - gentler reminder
  const getTimeRemaining = (purchaseDate = new Date()) => {
    const now = new Date();
    const expiryDate = new Date(purchaseDate);
    expiryDate.setDate(expiryDate.getDate() + 30); // 1 month = 30 days

    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { expired: true, text: 'Subscription ended', days: 0, gentle: true };

    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;

    if (months > 0) {
      return { expired: false, text: `Valid for ${months}mo ${days}d`, days: diffDays, gentle: false };
    }
    return { expired: false, text: `Valid for ${days} day${days !== 1 ? 's' : ''}`, days: diffDays, gentle: diffDays < 7 };
  };

  // Helper: Extract semester number from "6th sem" format
  const getSemesterNumber = (semesterStr) => {
    if (!semesterStr) return 0;
    const match = semesterStr.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  // Session-based welcome toast
  useEffect(() => {
    const hasShownWelcome = sessionStorage.getItem('welcomeToastShown');
    if (!hasShownWelcome) {
      const userName = user?.name?.split(' ')[0] || 'Student';
      const displayName = demoMode ? 'Demo User' : userName;

      setTimeout(() => {
        toast.success(`Welcome ${displayName}! 🎉`, {
          position: 'top-right',
          duration: 3000,
          style: {
            marginTop: '80px', // Below navbar
          }
        });
      }, 500);

      sessionStorage.setItem('welcomeToastShown', 'true');
    }
  }, [user, demoMode]);

  useEffect(() => {
    // Only fetch if we haven't fetched data yet in this session
    if (hasFetchedData.current) {
      // Data already fetched, just update demo mode
      setDemoMode(!isAuthenticated);
      return;
    }

    // First time loading - fetch data
    setLoading(true);
    if (isAuthenticated) {
      setDemoMode(false);
      fetchData();
    } else {
      setDemoMode(true);
      fetchDemoData();
    }
    hasFetchedData.current = true; // Mark as fetched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Only re-run when auth state changes

  const fetchData = async () => {
    try {
      const [coursesRes, myCoursesRes] = await Promise.all([
        courseAPI.getCourses(),
        courseAPI.getMyCourses(),
      ]);
      setCourses(coursesRes.data.data);
      const purchasedCourses = myCoursesRes.data.data.purchasedCourses;
      setMyCourses(purchasedCourses);

      // Validate activeCourseId - clear if not in purchased courses
      const validCourseIds = purchasedCourses.map(c => c._id);
      const isValid = validateAndClearIfInvalid(validCourseIds);

      if (!isValid) {
        toast.error('Please activate a course from your dashboard to continue.');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchDemoData = async () => {
    try {
      const [freeCoursesRes, allCoursesRes] = await Promise.all([
        courseAPI.getFreeCourses(),
        courseAPI.getCourses(),
      ]);

      const freeCourses = freeCoursesRes.data.data;
      setCourses(allCoursesRes.data.data);

      // Don't auto-activate any course - user must activate from dashboard
      // Validate if there's an existing activeCourseId
      const validCourseIds = freeCourses.map(c => c._id);
      validateAndClearIfInvalid(validCourseIds);

      setMyCourses(freeCourses);
    } catch (error) {
      console.error('Error fetching demo data:', error);
      toast.error('Failed to load demo courses');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateCourse = async (courseId) => {
    if (demoMode) {
      const courseData = myCourses.find(c => c._id === courseId);
      setActiveCourse(courseId, courseData);

      // Update recent order
      const currentRecent = getRecentOrder();
      const newRecent = [courseId, ...currentRecent.filter(id => id !== courseId)].slice(0, 8);
      localStorage.setItem('recentSubjects', JSON.stringify(newRecent));

      toast.dismiss(); // Dismiss previous toasts
      toast.success('Course activated! 🎯', {
        position: 'top-right',
        duration: 2000,
        style: {
          marginTop: '80px',
        }
      });
      return;
    }
    try {
      await courseAPI.activateCourse(courseId);
      const updatedUser = { ...user, activeCourse: courseId };
      updateUser(updatedUser);
      const courseData = myCourses.find(c => c._id === courseId);
      setActiveCourse(courseId, courseData);

      // Update recent order
      const currentRecent = getRecentOrder();
      const newRecent = [courseId, ...currentRecent.filter(id => id !== courseId)].slice(0, 8);
      localStorage.setItem('recentSubjects', JSON.stringify(newRecent));

      toast.dismiss(); // Dismiss previous toasts
      toast.success('Course activated! 🎯', {
        position: 'top-right',
        duration: 2000,
        style: {
          marginTop: '80px',
        }
      });

      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to activate course');
    }
  };

  const handlePurchaseCourse = async (courseId) => {
    if (demoMode) {
      toast.error('Please login to purchase courses!');
      navigate('/login');
      return;
    }
    try {
      await courseAPI.purchaseCourse(courseId);
      toast.success('Course purchased successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to purchase course');
    }
  };

  const navigateToPage = (path) => {
    if (demoMode) {
      navigate(path);
      return;
    }
    if (!user?.activeCourse) {
      toast.error('Please activate a course first!');
      return;
    }
    navigate(path);
  };

  // Get stats based on mode
  const getDisplayStats = () => {
    // Get user's primary faculty (from first purchased course or default to 'BCT')
    const userFaculty = myCourses.length > 0 ? myCourses[0].faculty : 'BCT';
    const facultySubjects = courses.filter(c => c.faculty === userFaculty).length;

    // Active courses - filter by dummy subscription (non-expired)
    const activeCourses = myCourses.filter(course => {
      const timeRemaining = getTimeRemaining(course.createdAt || new Date());
      return !timeRemaining.expired;
    });

    return {
      totalSubjects: courses.length,
      facultySubjects: facultySubjects,
      userFaculty: userFaculty,
      activeCourses: activeCourses.length,
      questionsAnalyzed: courses.reduce((sum, c) => sum + (c.questionCount || 0), 0),
    };
  };

  const displayStats = getDisplayStats();

  // Filter courses by selected semester with sorting for "Recent Subjects"
  const semesters = ['Recent', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];
  const filteredMyCourses = selectedSemester === 'Recent'
    ? [...myCourses].sort((a, b) => {
      const recentOrder = getRecentOrder();
      const indexA = recentOrder.indexOf(a._id);
      const indexB = recentOrder.indexOf(b._id);

      // If both are in recent list, sort by index
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      // If only A is in list, A comes first
      if (indexA !== -1) return -1;
      // If only B is in list, B comes first
      if (indexB !== -1) return 1;

      // Fallback to date sorting
      const dateA = new Date(a.createdAt || a.updatedAt || 0);
      const dateB = new Date(b.createdAt || b.updatedAt || 0);
      return dateB - dateA;
    }).slice(0, 8) // Hard limit to 8 recent items
    : myCourses.filter(c => {
      const semNum = getSemesterNumber(c.semester);
      const selectedNum = parseInt(selectedSemester);
      return semNum === selectedNum;
    });

  // Filter courses for modal
  const modalCourses = selectedSemester === 'Recent'
    ? courses
    : courses.filter(c => {
      const semNum = getSemesterNumber(c.semester);
      const selectedNum = parseInt(selectedSemester);
      return semNum === selectedNum;
    });


  // Removed loading screen - just show content immediately to prevent blink

  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 pt-24 pb-12 sm:px-6 lg:px-8">
        {/* Statistics Section - Simplified */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-6">
            <BarChart2 className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Statistics</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
            {/* Available BCT Subjects */}
            <div className="bg-white p-6 rounded-2xl border border-gray-400 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <Layers className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{displayStats.facultySubjects}</div>
                  <div className="text-sm text-gray-500 font-medium">Available {displayStats.userFaculty} Subjects</div>
                </div>
              </div>
            </div>

            {/* Active Courses */}
            <div className="bg-white p-6 rounded-2xl border border-gray-400 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center text-green-600">
                  <Target className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{displayStats.activeCourses}</div>
                  <div className="text-sm text-gray-500 font-medium">Active Courses</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Subjects Section */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-blue-600" />
              Subjects
            </h2>
          </div>

          {/* Semester Categories */}
          <div className="mb-6 overflow-x-auto pb-2">
            <div className="flex gap-2 min-w-max">
              {semesters.map((sem) => (
                <button
                  key={sem}
                  onClick={() => setSelectedSemester(sem)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${selectedSemester === sem
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {sem === 'Recent' ? 'Recent Subjects' : `${sem} Semester`}
                </button>
              ))}
            </div>
          </div>

          {filteredMyCourses.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-6">
                {filteredMyCourses.slice(0, selectedSemester === 'Recent' ? visibleCount : undefined).map((course) => {
                  const timeRemaining = getTimeRemaining(course.createdAt || new Date());
                  const isActive = activeCourseId === course._id;

                  return (
                    <div
                      key={course._id}
                      onClick={() => handleActivateCourse(course._id)}
                      className={`group bg-white rounded-2xl border-2 transition-all duration-300 cursor-pointer overflow-hidden relative max-w-[340px] w-full mx-auto ${isActive
                        ? 'border-green-500 shadow-lg shadow-green-500/30'
                        : 'border-gray-400 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1'
                        }`}
                    >
                      {/* Image */}
                      <div className="h-40 overflow-hidden relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent z-10"></div>
                        <img
                          src={course.image}
                          alt={course.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=300&fit=crop';
                          }}
                        />
                        {isActive && (
                          <div className="absolute top-3 right-3 z-20 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                            Active
                          </div>
                        )}
                      </div>

                      {/* Content - Title Below Image */}
                      <div className="p-4">
                        <h3 className="font-bold text-gray-900 text-base mb-3 line-clamp-1">
                          {course.name}
                        </h3>
                        <p className="text-xs text-gray-500 mb-4">{course.semester || 'N/A'} • {course.faculty || 'General'}</p>

                        {/* Compact Details */}
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-4 text-xs text-gray-600">
                          <div className="flex items-center gap-1.5">
                            <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                            <span>{course.questionCount || 0} Questions</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                            <span>{course.chapterCount || 0} Chapters</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-1 h-1 bg-purple-500 rounded-full"></span>
                            <span>{course.masterQuestionCount || 0} Master Qs</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-1 h-1 bg-orange-500 rounded-full"></span>
                            <span>{course.variationCount || 0} Variations</span>
                          </div>
                        </div>

                        {/* Subscription Status - Gentler */}
                        <div className={`flex items-center gap-2 mb-3 text-xs px-3 py-2 rounded-lg ${timeRemaining.gentle
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-gray-50 text-gray-600'
                          }`}>
                          <Clock className="w-3.5 h-3.5" />
                          <span className="font-medium">{timeRemaining.text}</span>
                        </div>

                        {/* Activate Button */}
                        <button
                          className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-colors ${isActive
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-900 text-white hover:bg-blue-600'
                            }`}
                        >
                          {isActive ? '✓ Activated' : 'Activate Course'}
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Add New Course Card */}
                {/* Add New Course Card - Hide in Recent Subjects */}
                {selectedSemester !== 'Recent' && (
                  <button
                    onClick={() => setShowModal(true)}
                    className="bg-gray-50 rounded-2xl border-2 border-dashed border-gray-400 flex flex-col items-center justify-center p-6 hover:border-blue-400 hover:bg-blue-50/50 transition-all group h-full min-h-[400px] max-w-[340px] w-full mx-auto"
                  >
                    <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Plus className="w-6 h-6 text-blue-500" />
                    </div>
                    <span className="font-semibold text-gray-600 group-hover:text-blue-600">Add New Course</span>
                    <span className="text-xs text-gray-400 mt-1">
                      {selectedSemester !== 'Recent' ? `From ${selectedSemester} Semester` : 'Browse all courses'}
                    </span>
                  </button>
                )}
              </div>

              {/* View More Button */}
              {selectedSemester === 'Recent' && filteredMyCourses.length > 4 && (
                <div className="mt-8 flex justify-center">
                  <button
                    onClick={() => setVisibleCount(prev => prev === 4 ? 8 : 4)}
                    className="flex items-center gap-2 px-6 py-2.5 bg-white border border-gray-300 rounded-full text-gray-600 font-medium hover:bg-gray-50 hover:border-blue-400 hover:text-blue-600 transition-all shadow-sm"
                  >
                    {visibleCount === 4 ? (
                      <>
                        <span>View More</span>
                        <ChevronRight className="w-4 h-4 rotate-90" />
                      </>
                    ) : (
                      <>
                        <span>Show Less</span>
                        <ChevronRight className="w-4 h-4 -rotate-90" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-2xl p-12 text-center border border-gray-400">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Subjects Found</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {selectedSemester !== 'Recent'
                  ? `No courses available for ${selectedSemester} semester. Try selecting a different category.`
                  : "You haven't enrolled in any courses yet. Browse our catalog to find the perfect subject for you."
                }
              </p>
              <button
                onClick={() => setShowModal(true)}
                className="btn-primary px-6 py-3 rounded-xl font-semibold shadow-lg shadow-blue-500/20"
              >
                Browse Courses
              </button>
            </div>
          )}
        </section>

        {/* Quick Actions Grid */}
        <section style={{ animationDelay: '0.2s' }}>
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-yellow-500" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <button
              onClick={() => navigateToPage('/master')}
              className="bg-white p-6 rounded-2xl border border-gray-400 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all text-left group"
            >
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-indigo-100 transition-colors">
                <Target className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">Analyze Questions</h3>
              <p className="text-sm text-gray-500">Deep dive into question patterns and difficulty distribution.</p>
            </button>

            <button
              onClick={() => navigateToPage('/filtered')}
              className="bg-white p-6 rounded-2xl border border-gray-400 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all text-left group"
            >
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition-colors">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">View Analytics</h3>
              <p className="text-sm text-gray-500">Explore detailed performance metrics and trends.</p>
            </button>

            <button
              onClick={() => setShowModal(true)}
              className="bg-white p-6 rounded-2xl border border-gray-400 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all text-left group"
            >
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors">
                <BookOpen className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">Browse Catalog</h3>
              <p className="text-sm text-gray-500">Discover {courses.length} courses to expand your knowledge.</p>
            </button>
          </div>
        </section>
      </main>

      {/* Course Modal - Faster Animation */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4 modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full p-8 relative max-h-[90vh] overflow-y-auto modal-content-fast" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-6 right-6 p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                {selectedSemester === 'Recent' ? 'All Courses' : `${selectedSemester} Semester Courses`}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {selectedSemester === 'Recent'
                  ? 'Browse our complete course catalog'
                  : `Courses available for ${selectedSemester} semester`
                }
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {modalCourses.map((course) => (
                <div key={course._id} className="bg-white border border-gray-400 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200 group hover:-translate-y-0.5">
                  {/* Image */}
                  <div className="h-32 overflow-hidden relative">
                    <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors z-10"></div>
                    <img
                      src={course.image}
                      alt={course.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=300&fit=crop';
                      }}
                    />
                  </div>

                  {/* Content - Compact */}
                  <div className="p-3">
                    {/* Title */}
                    <h3 className="text-sm font-bold text-gray-900 mb-0.5 line-clamp-1">{course.name}</h3>
                    <p className="text-xs text-gray-500 mb-2">{course.semester || 'General'} • {course.faculty}</p>

                    {/* Compact 2-Column Details */}
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1 mb-2.5 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                        <span>{course.questionCount} Qs</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-1 h-1 bg-purple-500 rounded-full"></span>
                        <span>₹{course.price}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => handlePurchaseCourse(course._id)}
                      className="w-full bg-gray-900 text-white py-2 rounded-lg text-xs font-semibold hover:bg-blue-600 transition-colors"
                    >
                      Enroll Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
